// The files attached to a message, and the "image N" pills in its text that
// point at its images (image-labels.ts). The Agent chat's posts and answers and
// the conversations of people draw them the same way.
//
// Promise:
// - Images show as thumbnails, shrunk whole into a 320 px box (never cut: a
//   tall screenshot gets narrow rather than losing its bottom). With the size
//   known the box is taken before the image loads, so the line does not move.
//   Videos sit among them the same way, as their first frame with ▶ and the
//   length. Other files are a chip with the name and the size, which downloads.
// - Pressing a video opens the full-screen video viewer, on any device (a
//   320 px box has no room for the inline player's controls). It opens again
//   where it was left.
// - Pressing a thumbnail or a pill shows the image large (the document's
//   viewer), with who put it there and when at the top left. Nothing opens a
//   new tab.
// - A link in the text to one of the message's own images is a pill: the
//   image's mark and "image N" in the accent color, no frame and no ground,
//   so the line keeps its height. N counts the message's images in order
//   (as the box did), whatever name the link has.
// - Hovering or focusing a pill outlines its thumbnail (inside it, so nothing
//   moves). When the text points at its images, each thumbnail carries its
//   number at the top left.
import { isVideoFileMime } from "@trameria/core/files-shared";
import { m } from "@trameria/i18n";
import {
  type CSSProperties,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import type { StaticBodyDress } from "../documents/blocks";
import {
  FileLightbox,
  formatVideoTime,
  openFileLightbox,
  openVideoViewer,
  PlayIcon,
  VideoViewer,
} from "../documents/blocks/media";
import {
  MEDIA_AT_ATTR,
  MEDIA_AUTHOR_ATTR,
  MEDIA_GALLERY_ATTR,
  MEDIA_REF_ATTR,
} from "../documents/blocks/media/gallery";
import { fileUrl, formatBytes } from "../documents/blocks/upload";
import { imageName, isImage } from "./image-labels";

export interface MessageFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  /** The image's size, when it was measured at upload. */
  width?: number | null;
  height?: number | null;
}

/** Who put the files there and when (as the caller shows the time). */
export interface AttachedBy {
  author: string;
  at: string;
}

/** Each image's number (its place among the images, from 1), by id in lowercase. */
export function imageNumbers(
  files: readonly { id: string; mime: string }[],
): Map<string, number> {
  const numbers = new Map<string, number>();
  for (const f of files)
    if (isImage(f.mime)) numbers.set(f.id.toLowerCase(), numbers.size + 1);
  return numbers;
}

/** Whether the text points at any of the images (then thumbnails are numbered). */
function textNamesImages(
  text: string,
  numbers: ReadonlyMap<string, number>,
): boolean {
  const lower = text.toLowerCase();
  for (const id of numbers.keys())
    if (lower.includes(`trameria://file/${id}`)) return true;
  return false;
}

/** The thumbnail's box (px): the image shrunk whole into it, never stretched. */
const THUMBNAIL_MAX = 320;

export function thumbnailSize(
  width: number,
  height: number,
): { width: number; height: number } {
  const scale = Math.min(1, THUMBNAIL_MAX / width, THUMBNAIL_MAX / height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

/**
 * The outline of a thumbnail whose pill is hovered. Drawn inside: in the post
 * box the chips sit in a row that scrolls sideways, which would cut an
 * outline drawn outside.
 */
export const LINKED_OUTLINE =
  "data-[linked]:outline-2 data-[linked]:-outline-offset-2 data-[linked]:outline-accent";

/** A pill's image mark (11 px, the color of its text). */
export const IMAGE_MARK_SVG =
  '<svg aria-hidden="true" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" class="pointer-events-none mr-0.5 inline-block h-[11px] w-[11px] align-[-1px]">' +
  '<rect x="1.5" y="2" width="9" height="8" rx="1.5"></rect>' +
  '<circle cx="4.5" cy="4.8" r="0.9"></circle>' +
  '<path d="M2 9l2.8-2.6 2 1.8 1.4-1.2L10 8.6"></path></svg>';

/** The pill's look in a drawn text (a button; the mark goes before the name). */
const PILL_CLASS =
  "cursor-zoom-in bg-transparent p-0 text-accent hover:underline [-webkit-touch-callout:none]";

const PILL_ATTR = "data-attachment-ref";

/**
 * Turns the links to the message's own images into pills (after the body's
 * files were dressed as chips), then the caller's dressing.
 */
function pillDress(
  numbers: ReadonlyMap<string, number>,
  then: StaticBodyDress | undefined,
): StaticBodyDress {
  return (root) => {
    for (const span of root.querySelectorAll<HTMLElement>(
      "span[data-trameria-file-inline]",
    )) {
      const id = (
        span.getAttribute("data-trameria-file-inline") ?? ""
      ).toLowerCase();
      const n = numbers.get(id);
      if (n === undefined) continue;
      const doc = span.ownerDocument;
      const pill = doc.createElement("button");
      pill.type = "button";
      pill.setAttribute(PILL_ATTR, id);
      // The viewer steps from here through the message's images (gallery.ts).
      pill.setAttribute(MEDIA_REF_ATTR, "");
      pill.setAttribute(
        "aria-label",
        m.blocks_file_enlarge_named({ name: imageName(n) }),
      );
      pill.className = PILL_CLASS;
      pill.innerHTML = IMAGE_MARK_SVG;
      pill.append(imageName(n));
      span.replaceChildren(pill);
    }
    then?.(root);
  };
}

/** The image shown large, from its thumbnail or its pill. */
function openImage(
  from: Element,
  apiBase: string,
  file: MessageFile,
  by: AttachedBy | undefined,
): void {
  openFileLightbox(from, {
    src: fileUrl(apiBase, file.id),
    name: file.name,
    download: fileUrl(apiBase, file.id, { download: true }),
    author: by?.author,
    at: by?.at,
  });
}

/**
 * A message's text and its files, the pills in the text tied to the
 * thumbnails under it. `listed` are the files shown under the text (the
 * caller leaves out what the text draws itself); the numbers count all of
 * `files`.
 */
export function AttachedText({
  text,
  files,
  listed = files,
  by,
  dress,
  markdown,
}: {
  text: string;
  files: readonly MessageFile[];
  listed?: readonly MessageFile[];
  by?: AttachedBy;
  /** What the caller adds to the drawn text (a stable function). */
  dress?: StaticBodyDress;
  /** Draws the text with the given dressing. */
  markdown(dress: StaticBodyDress | undefined): ReactNode;
}) {
  const { apiBase } = useMeApp();
  const [linked, setLinked] = useState<string | null>(null);
  // Made again only when the images or the caller's dressing change: a new
  // function draws the text again.
  const imageKey = files
    .filter((f) => isImage(f.mime))
    .map((f) => f.id)
    .join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `imageKey` stands for the images in `files`
  const numbers = useMemo(() => imageNumbers(files), [imageKey]);
  const dressed = useMemo(
    () => (numbers.size > 0 ? pillDress(numbers, dress) : dress),
    [numbers, dress],
  );
  const pillOf = (target: EventTarget) =>
    target instanceof Element
      ? target.closest<HTMLElement>(`[${PILL_ATTR}]`)
      : null;
  const light = (e: MouseEvent | FocusEvent) =>
    setLinked(pillOf(e.target)?.getAttribute(PILL_ATTR) ?? null);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a delegation point for the pills drawn in the text (buttons, reachable by keyboard themselves)
    // biome-ignore lint/a11y/useKeyWithClickEvents: as above (Enter and Space press the pill's button)
    <div
      // The images of the text and the attached ones step together in the viewer.
      {...galleryAttrs(by)}
      onMouseOver={light}
      onMouseLeave={() => setLinked(null)}
      onFocus={light}
      onBlur={() => setLinked(null)}
      onClick={(e) => {
        const pill = pillOf(e.target);
        const id = pill?.getAttribute(PILL_ATTR);
        const file = id && files.find((f) => f.id.toLowerCase() === id);
        if (!pill || !file) return;
        setLinked(null);
        openImage(pill, apiBase, file, by);
      }}
    >
      {text.trim() !== "" && markdown(dressed)}
      <MessageFiles
        files={listed}
        by={by}
        numbers={textNamesImages(text, numbers) ? numbers : undefined}
        linked={linked}
      />
    </div>
  );
}

/** Marks a message as one gallery for the viewer, with who put its images there. */
function galleryAttrs(by: AttachedBy | undefined): Record<string, string> {
  return {
    [MEDIA_GALLERY_ATTR]: "",
    ...(by ? { [MEDIA_AUTHOR_ATTR]: by.author, [MEDIA_AT_ATTR]: by.at } : {}),
  };
}

/** The number over a thumbnail's corner: on the image, so a dark see-through ground in either theme. */
function NumberBadge({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute top-1 left-1 min-w-4 rounded-[4px] bg-black/55 px-1 text-center text-[11px] leading-4 text-white tabular-nums"
    >
      {n}
    </span>
  );
}

/**
 * The files under a message: thumbnails, then chips. `numbers` (when the text
 * points at the images) puts each image's number on its thumbnail; `linked`
 * is the image whose pill is hovered.
 */
export function MessageFiles({
  files,
  by,
  numbers,
  linked = null,
}: {
  files: readonly MessageFile[];
  by?: AttachedBy;
  numbers?: ReadonlyMap<string, number>;
  linked?: string | null;
}) {
  const { apiBase } = useMeApp();
  if (files.length === 0) return null;
  const shown = files.filter((f) => isImage(f.mime) || isVideoFileMime(f.mime));
  const others = files.filter((f) => !shown.includes(f));
  return (
    <div className="mt-1.5 flex flex-col gap-1.5" {...galleryAttrs(by)}>
      {shown.length > 0 && (
        // Each button as large as its image (a stretched one would put a
        // short image in the middle of a tall frame).
        <div className="flex flex-wrap items-start gap-1.5">
          {shown.map((f) => {
            if (!isImage(f.mime)) return <MessageVideo key={f.id} file={f} />;
            const n = numbers?.get(f.id.toLowerCase());
            const size =
              f.width && f.height ? thumbnailSize(f.width, f.height) : null;
            return (
              <button
                type="button"
                key={f.id}
                // One of the message's images in the viewer (gallery.ts).
                data-media-item=""
                data-linked={linked === f.id.toLowerCase() ? "" : undefined}
                aria-label={m.blocks_file_enlarge_named({
                  name: n === undefined ? f.name : `${imageName(n)} ${f.name}`,
                })}
                onClick={(e) => openImage(e.currentTarget, apiBase, f, by)}
                className={`relative block max-w-[min(320px,100%)] cursor-zoom-in overflow-hidden rounded-[6px] border border-border bg-transparent p-0 ${LINKED_OUTLINE}`}
              >
                {n !== undefined && <NumberBadge n={n} />}
                <img
                  src={fileUrl(apiBase, f.id)}
                  alt={f.name}
                  loading="lazy"
                  // The box and the image have the same ratio; the button
                  // takes the press (the image is never the target).
                  className="pointer-events-none block h-auto max-h-[320px] w-auto max-w-full object-contain"
                  style={
                    size && f.width && f.height
                      ? {
                          aspectRatio: `${f.width} / ${f.height}`,
                          width: size.width,
                        }
                      : undefined
                  }
                />
              </button>
            );
          })}
          {/* The viewers' hosts; several on a screen open one viewer (`claim`). */}
          {shown.some((f) => !isImage(f.mime)) && <VideoViewer />}
          <FileLightbox />
        </div>
      )}
      {others.map((f) => (
        <a
          key={f.id}
          href={fileUrl(apiBase, f.id, { download: true })}
          className="inline-flex w-fit max-w-full items-center gap-2 rounded-[5px] border border-border px-2 py-1 text-[12.5px] text-fg hover:bg-hover"
        >
          <span className="truncate">{f.name}</span>
          <span className="flex-none text-faint">{formatBytes(f.size)}</span>
        </a>
      ))}
    </div>
  );
}

/** A video under a message: a box like an image's (16:9 until its size is known). */
function MessageVideo({ file: f }: { file: MessageFile }) {
  const { apiBase } = useMeApp();
  const [loaded, setLoaded] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const known =
    f.width && f.height ? { width: f.width, height: f.height } : loaded;
  const { width, height } = known ?? { width: 16, height: 9 };
  return (
    <VideoStill
      src={fileUrl(apiBase, f.id)}
      name={f.name}
      download={fileUrl(apiBase, f.id, { download: true })}
      className="relative block h-auto max-w-[min(320px,100%)] overflow-hidden rounded-[6px] border border-border bg-black p-0"
      style={{
        aspectRatio: `${width} / ${height}`,
        width: thumbnailSize(width, height).width,
      }}
      onSize={known ? undefined : (w, h) => setLoaded({ width: w, height: h })}
    />
  );
}

/**
 * A video as a still: its first frame and ▶, and its length unless `compact`.
 * Pressing it opens the full-screen video viewer, again where it was left.
 */
export function VideoStill({
  src,
  name,
  download,
  compact = false,
  className,
  videoClassName = "object-contain",
  style,
  onSize,
}: {
  src: string;
  name: string;
  download?: string;
  /** A chip's size: a small ▶ and no length. */
  compact?: boolean;
  /** The button's box. */
  className: string;
  videoClassName?: string;
  style?: CSSProperties;
  /** The video's size, once its metadata is in. */
  onSize?(width: number, height: number): void;
}) {
  const [duration, setDuration] = useState(0);
  const at = useRef(0);
  return (
    <button
      type="button"
      aria-label={m.media_video_play_named({ name })}
      className={`cursor-pointer ${className}`}
      style={style}
      onClick={(e) =>
        openVideoViewer(e.currentTarget, {
          src,
          name,
          download,
          startAt: at.current,
          onClose: (t) => {
            at.current = t;
          },
        })
      }
    >
      <video
        // `#t=0.001` makes iOS Safari paint the first frame (black without it).
        src={`${src}#t=0.001`}
        preload="metadata"
        muted
        playsInline
        tabIndex={-1}
        aria-hidden="true"
        className={`pointer-events-none block h-full w-full ${videoClassName}`}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          setDuration(v.duration);
          if (v.videoWidth > 0 && v.videoHeight > 0)
            onSize?.(v.videoWidth, v.videoHeight);
        }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span
          className={`flex items-center justify-center rounded-full bg-black/55 text-white ${compact ? "size-4" : "size-11"}`}
        >
          <PlayIcon size={compact ? 10 : 22} />
        </span>
      </span>
      {!compact && Number.isFinite(duration) && duration > 0 && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-1.5 bottom-1.5 rounded-[4px] bg-black/65 px-1.5 text-[12px] leading-[18px] text-white tabular-nums"
        >
          {formatVideoTime(duration)}
        </span>
      )}
    </button>
  );
}
